import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import PluginCardC from '../components/PluginCardC';
import NewsletterSimpleC from '../components/NewsletterSimpleC';
import BannerSlider from '../components/BannerSlider';
import { NewsCompactGrid } from '../components';
import { useTranslation } from '../hooks/useTranslation';
import { useOwnedProductIds } from '../hooks/useOwnedProductIds';

interface Plugin {
  id: string;
  name: string;
  slug: string;
  description: string;
  detailedDescription?: string;
  price: number;
  version: string;
  imageUrl?: string;
  features?: string[];
  compatibility: string[];
  tags: string[];
  isActive: boolean;
}

function Products() {
  const { t, currentLanguage } = useTranslation();
  const ownedIds = useOwnedProductIds();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Plugin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isNewsletterSubscribed, setIsNewsletterSubscribed] = useState(false);

  useEffect(() => {
    // Проверяем авторизацию
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    
    // Проверяем подписку на newsletter (только флаг, без email)
    const newsletterSubscribed = localStorage.getItem('vst_shop_newsletter_subscribed');
    setIsNewsletterSubscribed(newsletterSubscribed === 'true');
    
    // Загружаем плагины
    axios.get(`/api/plugins?lang=${currentLanguage}`)
      .then(res => {
        setProducts(res.data.data || res.data);
        setLoading(false);
      })
      .catch(err => {
        setError(t('errors.failed_to_fetch_products'));
        setLoading(false);
      });
  }, [currentLanguage]);

  const handleBuyNow = (_pluginId: string, slug: string) => {
    navigate(`/plugins/${slug}`);
  };

  const handleViewDetails = (slug: string) => {
    // Navigate to plugin detail page
    navigate(`/plugins/${slug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#a855f7' }} />
          <p className="text-white mt-4">{t('products.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Banner Slider - Full Width */}
      <div className="-mx-4">
      <BannerSlider />
      </div>
      
      {/* Hero Section */}
      <section 
        className="relative py-20 overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/backgrounds/products.gif)'
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-pink-600/30" />
        <div className="container mx-auto px-6 text-center relative z-10 flex flex-col justify-center h-full" style={{ paddingTop: '80px' }}>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in mt-24">
            {t('products.hero_title')}
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto animate-fade-in">
            {t('products.hero_description')}
          </p>
          <div className="animate-fade-in">
            <button 
              onClick={() => navigate('/plugins')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-3 rounded-md text-lg font-semibold transition-all duration-300"
            >
              {t('products.view_catalog_button')}
            </button>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <h3 className="text-3xl font-bold text-white mb-8 text-center">
            {t('products.our_plugins_title')} <span className="gradient-text">{t('products.plugins_word')}</span>
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {products.map((plugin) => (
              <PluginCardC
                key={plugin.id}
                id={plugin.id}
                name={plugin.name}
                price={plugin.price}
                imageUrl={plugin.imageUrl}
                description={plugin.description}
                version={plugin.version}
                compatibility={plugin.compatibility}
                features={plugin.features || []}
                slug={plugin.slug}
                tags={plugin.tags}
                owned={ownedIds.has(String(plugin.id))}
                onBuyNow={handleBuyNow}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* See All Plugins Button */}
          <div className="text-center">
            <button
              onClick={() => navigate('/plugins')}
              className="inline-flex items-center border border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white px-8 py-3 rounded-lg transition-colors duration-200"
            >
              {t('products.see_all_plugins')}
              <svg 
                className="ml-2 w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* News Compact Grid - Full Width */}
      <div className="-mx-4">
      <NewsCompactGrid />
      </div>

      {/* Newsletter Section - Full Width - показываем только неавторизованным неподписанным */}
      {!isAuthenticated && !isNewsletterSubscribed && (
        <div className="-mx-4">
          <NewsletterSimpleC />
        </div>
      )}
    </div>
  );
}

export default Products; 